import '../../styles/_modal.scss';
import '../../styles/_tutorial.scss';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import DOMPurify from 'dompurify';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';

import { PrePlayerModalStep, TutorialListItem } from '../../datasource/generated';
import { useGetTutorialsQuery } from '../../datasource/queries/tutorials';
import { ScreenSizeQueries } from '../../enums/ScreenSizeQueries';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useDownload from '../../hooks/useDownload';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import Button from '../Button/Button';
import { ErrorContainer } from '../MainError/MainError';
import { NavBar } from '../NavBar/NavBar';
import withPLIErrorBoundary from '../PliErrorBoundary/PliErrorBoundary';
import { setShowDownloadTutorial } from '../Store/downloadSlice';
import {
    setIsLicenseReviewComplete,
    setIsTutorialComplete,
} from '../Store/prePlayerSlice';
import GroupcastAnimation from './Animation/GroupcastAnimation';
import Mp3Mp4Animation from './Animation/Mp3Mp4Animation';
import VerifyAttendanceAnimation from './Animation/VerifyAttendanceAnimation';
import { GroupcastTutorialSteps } from './GroupcastTutorial';
import { Mp3DownloadTutorialSteps } from './Mp3DownloadTutorial';
import { Mp3TutorialSteps } from './Mp3Tutorial';
import { Mp4DownloadTutorialSteps } from './Mp4DownloadTutorial';
import { Mp4TutorialSteps } from './Mp4Tutorial';
import PrePlayerAnimationVideo, { selectPrePlayerVideo } from './PrePlayerAnimationVideo';
import { TutorialType, tutorialTypeCookiesMap } from './TutorialType';
import { VerifyAttendanceTutorialSteps } from './VerifyAttendanceTutorial';

export interface IStepInformation {
    headerText: string;
    bodyText: string;
    buttonLabel?: string | null;
    animation?: PrePlayerModalStep;
}
export { TutorialType, tutorialTypeCookiesMap };

export function mergeTutorialSteps(
    steps: Array<IStepInformation>,
    tutorialStepsFromApi?: Array<TutorialListItem> | null,
    prePlayerModalSteps?: Array<PrePlayerModalStep> | null,
) {
    return steps.map((step, index) => ({
        ...step,
        headerText: prePlayerModalSteps?.[index]?.heading
            ?? tutorialStepsFromApi?.[index]?.headerText
            ?? step.headerText,
        bodyText: prePlayerModalSteps?.[index]?.body
            ?? tutorialStepsFromApi?.[index]?.bodyText
            ?? step.bodyText,
        buttonLabel: prePlayerModalSteps?.[index]?.buttonLabel,
        animation: prePlayerModalSteps?.[index],
    }));
}

export interface ITutorialProps {
    type: TutorialType;
    onTutorialFinish: () => void;
    useContentFromApi: boolean;
    prePlayerModalSteps?: Array<PrePlayerModalStep> | null;
    modalAppElement?: string;
}


export const TutorialBase = ({
    type,
    onTutorialFinish,
    useContentFromApi,
    prePlayerModalSteps,
    modalAppElement,
}: ITutorialProps) => {
    const dispatch = useAppDispatch();
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const { data: tutorials } = useGetTutorialsQuery(!useContentFromApi ? skipToken : undefined);
    const showPreplayerTutorial = useAppSelector((state) => state.prePlayer.showTutorial.find(x => x.tutorialType == type)?.showTutorial) ?? true;
    const isPreplayerTutorialComplete = useAppSelector((state) => state.prePlayer.isTutorialComplete);
    const showDownloadTutorial = useAppSelector((state) => state.download.showDownloadTutorial);
    const { handleDownloadTutorialFinish } = useDownload();
    const isXs = useMediaQuery(ScreenSizeQueries.SMALL);

    const cookieOptions = useMemo(() => ({
        sameSite: 'strict',
        expires: 180
    }), []);

    useEffect(() => {
        dispatch(setIsTutorialComplete(!showPreplayerTutorial));
    }, [dispatch, showPreplayerTutorial]);

    const buildTutorialSteps = useCallback((type: TutorialType) => {
        const tutorialTypeStepsMap = {
            [TutorialType.VerifyAttendanceTutorial]: VerifyAttendanceTutorialSteps, 
            [TutorialType.GroupcastTutorial]: GroupcastTutorialSteps,
            [TutorialType.Mp3Tutorial]: Mp3TutorialSteps,
            [TutorialType.Mp4Tutorial]: Mp4TutorialSteps,
            [TutorialType.Mp3DownloadTutorial]: Mp3DownloadTutorialSteps,
            [TutorialType.Mp4DownloadTutorial]: Mp4DownloadTutorialSteps,
        };
        
        const tutorialStepsFromApi = useContentFromApi
            ? tutorials?.find(t => t.name == type)?.tutorialItems
            : undefined;
        const cmsPrePlayerSteps = type === TutorialType.VerifyAttendanceTutorial
            ? prePlayerModalSteps
            : undefined;

        return mergeTutorialSteps(
            tutorialTypeStepsMap[type],
            tutorialStepsFromApi,
            cmsPrePlayerSteps);
    }, [prePlayerModalSteps, useContentFromApi, tutorials]);

    const downloadTutorialType = launchDetails.isPortable && launchDetails.isAudioOnly ?
        TutorialType.Mp3DownloadTutorial :
        TutorialType.Mp4DownloadTutorial;
    const selectedTutorialType = showDownloadTutorial ? downloadTutorialType : type;
    const steps = useMemo(
        () => buildTutorialSteps(selectedTutorialType),
        [buildTutorialSteps, selectedTutorialType]);

    const [stepIndex, setStepIndex] = useState(0);

    const isLastStep = useMemo(() => {
        return stepIndex === steps?.length - 1;
    }, [steps, stepIndex]);

    const isFirstStep = useMemo(() => {
        return stepIndex === 0;
    }, [stepIndex]);

    const nextStep = useCallback(() => {
        const setCookie = () => {
            if (!showDownloadTutorial && type !== TutorialType.GroupcastTutorial) {
                Cookies.set(tutorialTypeCookiesMap[type], new Date().toDateString(), cookieOptions);
            }
        };

        if (!isLastStep)
            setStepIndex(stepIndex + 1);
        else {
            type === TutorialType.GroupcastTutorial && dispatch(setIsLicenseReviewComplete(true));
            dispatch(setIsTutorialComplete(true));
            showDownloadTutorial ? handleDownloadTutorialFinish() : onTutorialFinish();
            setCookie();
            setStepIndex(0);
            dispatch(setShowDownloadTutorial(false));
        }
    }, [isLastStep, stepIndex, showDownloadTutorial, type, cookieOptions, dispatch, handleDownloadTutorialFinish, onTutorialFinish]);

    const prevStep = useCallback(() => {
        if (!isFirstStep) {
            setStepIndex(stepIndex - 1);
        }
    }, [stepIndex, isFirstStep]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
            prevStep();
        }
        if (e.key === 'ArrowRight') {
            nextStep();
        }
    }, [nextStep, prevStep]);

    useEffect(() => {
        if (!isPreplayerTutorialComplete) {
            window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [handleKeyDown, isPreplayerTutorialComplete]);

    const stickyButtonLabels = useMemo(() => {
        const nextButtonLabels = {
            label: 'Next', ariaLabel: 'Next step',
        };
        const downloadButtonLabels = {
            label: 'Download', ariaLabel: 'Download media',
        };
        const startWatchingButtonLabels = {
            label: 'Start watching', ariaLabel: 'Start watching',
        };

        if (showDownloadTutorial) return downloadButtonLabels;
        if (type === TutorialType.GroupcastTutorial && isLastStep) return startWatchingButtonLabels;
        const cmsButtonLabel = steps[stepIndex]?.buttonLabel;
        if (cmsButtonLabel) {
            return {
                label: cmsButtonLabel,
                ariaLabel: cmsButtonLabel,
            };
        }
        return nextButtonLabels;
    }, [isLastStep, showDownloadTutorial, stepIndex, steps, type]);

    const animationVideo = useMemo(() => {
        return selectPrePlayerVideo(steps[stepIndex]?.animation, isXs);
    }, [isXs, stepIndex, steps]);

    const sanitizedBodyText = useMemo(
        () => DOMPurify.sanitize(steps[stepIndex]?.bodyText ?? ''),
        [stepIndex, steps]);

    const navBackButtonAriaLabel = 'Return to previous step';
    const navCloseButtonAriaLabel = 'Close';

    useEffect(() => {
        ReactModal.setAppElement(modalAppElement ?? '#root');
    }, [modalAppElement]);

    if (!steps?.length) return <></>;
    return ( 

        <ReactModal
            isOpen={!isPreplayerTutorialComplete || showDownloadTutorial} 
            key={stepIndex}
            className={'watch-modal tutorial-modal'}
            overlayClassName={'watch-modal__overlay'} >

            <div className={'watch-modal__nav'}>
                <NavBar.Root mode={showDownloadTutorial ? 'close' : 'back'} containerMode='modal'>
                    {!isFirstStep &&
                        <NavBar.BackButton onClick={prevStep} aria-label={navBackButtonAriaLabel} isShowing />
                    }
                    {showDownloadTutorial &&
                        <NavBar.CloseButton onClick={() => dispatch(setShowDownloadTutorial(false))} aria-label={navCloseButtonAriaLabel} isShowing />
                    }
                </NavBar.Root>
            </div >

            <div className='watch-modal__content tutorial-modal__content'>

                {
                    type === TutorialType.VerifyAttendanceTutorial && animationVideo ? (
                        <PrePlayerAnimationVideo
                            key={`${stepIndex}-${animationVideo.src}`}
                            src={animationVideo.src}
                            playCount={animationVideo.playCount}
                            endOnLastFrame={animationVideo.endOnLastFrame}
                        />
                    ) : type === TutorialType.VerifyAttendanceTutorial ? (
                        <VerifyAttendanceAnimation 
                            stepIndex={stepIndex}
                        />
                    ) : type === TutorialType.GroupcastTutorial ? (
                        <GroupcastAnimation 
                            stepIndex={stepIndex}
                        />
                    ) : type === TutorialType.Mp4Tutorial || type === TutorialType.Mp3Tutorial ? (
                        <Mp3Mp4Animation 
                            stepIndex={stepIndex}
                            isMp4={type === TutorialType.Mp4Tutorial}
                        />
                    ) : null
                }
                <div className='tutorial-modal__content__text-block'>
                    <div className='tutorial-modal__content__text-block__header-with-eyebrow'>
                        {
                            steps.length > 1 &&
                            <div className='paragraph-2 text--medium-grey'>
                                {stepIndex + 1} of {steps.length}
                            </div>
                        }
                        <h1 className='heading-4 mb-0'>
                            {steps[stepIndex]?.headerText}
                        </h1>
                    </div>
                    <div className='paragraph-1'
                        dangerouslySetInnerHTML={{ __html: sanitizedBodyText }} />
                </div>
            </div>

            <div className='watch-modal__footer'>
                <div className='watch-modal__footer__button-container'>
                    <Button buttonClass='sticky-button'
                        label={stickyButtonLabels.label}
                        aria-label={stickyButtonLabels.ariaLabel}
                        action={nextStep} />
                </div>
            </div>

        </ReactModal >

    );
};

/**
 * Default export: component already wrapped with the Error Boundary HOC.
 * - Uses MainError as fallback (Standalone layout).
 * - Renders only the error page without other app components.
 */
export const Tutorial = withPLIErrorBoundary(TutorialBase, null, {
    errorContainer: ErrorContainer.PlayerModal,
    title: 'Sorry, we can\'t load the tutorial.',
});

export default Tutorial;
